import React, { useState, useRef, useEffect } from 'react';
import { Popup, Position, ScrollView } from '../../components/BootstrapControls';
import DataGrid, { Column, LoadPanel, Scrolling, Selection, Lookup } from '../../components/BootstrapDataGrid';
import Axios from '../../api/axios';
import notify from '../../utils/notifyUtil';
import Dict from '../../messages.js';
import { getIslemStTur } from '../../utils/satisTipiSabit.js';
import { allSatisPaketLookupItems, allSatisTuruLookupItems, satisTipiItems } from '../../utils/satisTipiLookups.js';
import IslemPanel from '../../components/cari/IslemPanel';
import MasterKayitGerekliUyari, { cariKayitliMi, MSG_ONCE_SATIS_OLUSTUR } from '../../components/cari/MasterKayitGerekliUyari.js';
import { subscribeCariSync } from '../../utils/cariSyncBus.js';
import { getCachedOrFetch, setCached, clearCached, carRefCacheKey, casRefCacheKey } from '../../utils/requestCache.js';
import { enableGridWheelOuterScroll } from '../../utils/gridWheelOuterScroll.js';

export default function IslemGirPanel({ carRef, carAdSoy, formShow, onFormKapandi, embedded = false, inTabMode = false, refreshToken = 0 }) {
    const [satisList, setSatisList] = useState([]);
    const [activeSatis, setActiveSatis] = useState(null);
    const [satisLoaded, setSatisLoaded] = useState(false);
    const [islemRefreshToken, setIslemRefreshToken] = useState(0);
    const gridRef = useRef(null);
    const activeSatisRef = useRef(null);
    const isMountedRef = useRef(true);
    const hasLoadedRef = useRef(false);
    const lastLoadKeyRef = useRef('');
    const rootScrollContainerRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    useEffect(() => {
        const cleanup = enableGridWheelOuterScroll(rootScrollContainerRef.current);
        return cleanup;
    }, []);

    // Synchronization effect - listen for cari sync events
    useEffect(() => {
        const unsub = subscribeCariSync((evt) => {
            if (!carRef) return;
            if (Number(evt?.carRef) !== Number(carRef)) return;
            
            if (evt?.source === 'islem') {
                const casRef = evt?.casRef;
                if (casRef) clearCached(casRefCacheKey('ISLEMLIST', casRef));
                const hasIslemPayload = evt?.islemChanges && (Array.isArray(evt?.islemChanges?.inserted) || Array.isArray(evt?.islemChanges?.updated) || Array.isArray(evt?.islemChanges?.deleted));
                if ((!hasIslemPayload) && (!casRef || Number(casRef) === Number(activeSatisRef.current?.CAS_REF))) {
                    setIslemRefreshToken((v) => v + 1);
                }
            }
            if (evt?.source === 'satis') {
                if (Array.isArray(evt?.satisList)) {
                    const rows = evt.satisList;
                    setSatisList(rows);
                    if (rows.length > 0) setActiveSatis(rows[0]);
                    else setActiveSatis(null);
                    if (carRef) setCached(carRefCacheKey('SATIS_LST', carRef), rows);
                    setSatisLoaded(true);
                    return;
                }
                hasLoadedRef.current = false;
                lastLoadKeyRef.current = '';
                if (carRef) clearCached(carRefCacheKey('SATIS_LST', carRef));
                fetchSatisList();
            }
        });
        return () => unsub();
    }, [carRef]);

    useEffect(() => {
        if (!formShow || !cariKayitliMi(carRef)) return;
        const loadKey = `${carRef}|${refreshToken}`;
        if (lastLoadKeyRef.current === loadKey && hasLoadedRef.current) return;
        setSatisLoaded(false);
        fetchSatisList();
        hasLoadedRef.current = true;
        lastLoadKeyRef.current = loadKey;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formShow, carRef, refreshToken]);

    const fetchSatisList = () => {
        if (!carRef || carRef <= 0) { setSatisList([]); return; }
        const cacheKey = carRefCacheKey('SATIS_LST', carRef);
        getCachedOrFetch(cacheKey, async () => {
            const res = await Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/lst?CAR_REF=${carRef}`);
            return Array.isArray(res?.data) ? res.data : [];
        })
            .then((list) => {
                if (!isMountedRef.current) return;
                const satisRows = Array.isArray(list) ? list : [];
                setSatisList(satisRows);
                if (satisRows.length > 0) setActiveSatis(satisRows[0]);
                else setActiveSatis(null);
                setSatisLoaded(true);
            }).catch(err => {
                if (!isMountedRef.current) return;
                notify(Dict.get('serverError'), 'error');
                setSatisLoaded(true);
            });
    };

    activeSatisRef.current = activeSatis;

    const handleHiding = () => {
        hasLoadedRef.current = false;
        lastLoadKeyRef.current = '';
        setSatisList([]);
        setActiveSatis(null);
        setSatisLoaded(false);
        if (onFormKapandi) onFormKapandi();
    };

    const isEmbedded = embedded || inTabMode;

    if (!cariKayitliMi(carRef)) {
        const uyariContent = <MasterKayitGerekliUyari carRef={carRef} sekmeAdi="İşlem Girişi" icon="📋" />;
        if (isEmbedded) {
            return <div style={{ width: '100%', height: '100%' }}>{uyariContent}</div>;
        }
        return (
            <Popup
                visible={formShow}
                onHiding={handleHiding}
                dragEnabled={true}
                showCloseButton={true}
                width="100%"
                height="100%"
                shading={false}
                title={`İşlem Girişi (${carRef || ''}${carAdSoy ? ' - ' + carAdSoy : ''})`}
            >
                <Position my="center" at="center" of={window} />
                {uyariContent}
            </Popup>
        );
    }

    if (satisLoaded && (!satisList || satisList.length === 0)) {
        const uyariContent = <MasterKayitGerekliUyari carRef={carRef} sekmeAdi="İşlem Girişi" icon="📋" baslik="Satış Kaydı Gerekli" mesaj={MSG_ONCE_SATIS_OLUSTUR} />;
        if (isEmbedded) {
            return <div style={{ width: '100%', height: '100%' }}>{uyariContent}</div>;
        }
        return (
            <Popup
                visible={formShow}
                onHiding={handleHiding}
                dragEnabled={true}
                showCloseButton={true}
                width="100%"
                height="100%"
                shading={false}
                title={`İşlem Girişi (${carRef || ''}${carAdSoy ? ' - ' + carAdSoy : ''})`}
            >
                <Position my="center" at="center" of={window} />
                {uyariContent}
            </Popup>
        );
    }

    const content = (
        <div ref={rootScrollContainerRef} style={{ width: '100%', height: '100%' }}>
        <ScrollView width="100%" height="100%">
            <div style={{ padding: 12 }}>
                    {/* Satışlar Gridi */}
                    <div style={{
                        marginBottom: 16,
                        padding: 12,
                        background: 'var(--dx-color-bg-secondary, #f8f9fa)',
                        border: '1px solid var(--dx-color-border, #dee2e6)',
                        borderRadius: 4
                    }}>
                        <div style={{
                            marginBottom: 8,
                            fontWeight: 'bold',
                            fontSize: 14,
                            color: 'var(--dx-color-text, #212529)',
                            borderBottom: '2px solid var(--dx-color-primary, #1976d2)',
                            paddingBottom: 4
                        }}>
                            📊 Satış Kayıtları
                        </div>
                        {(!satisList || satisList.length === 0) ? (
                            <div style={{
                                padding: 24,
                                textAlign: 'center',
                                background: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: 4,
                                color: '#856404',
                                fontSize: 14
                            }}>
                                Öncelikle Satış sekmesinden satış kaydı oluşturunuz.
                            </div>
                        ) : (
                        <DataGrid
                            dataSource={satisList}
                            showBorders={true}
                            keyExpr="CAS_REF"
                            focusedRowEnabled={true}
                            hoverStateEnabled={false}
                            repaintChangesOnly={true}
                            ref={gridRef}
                            height={Math.round((5 * 34) + 56 + 10)}
                            width="100%"
                            defaultFocusedRowIndex={0}
                            columnAutoWidth={true}
                            allowColumnResizing={true}
                            onFocusedRowChanged={(e) => {
                                const active = e.row?.data || null;
                                setActiveSatis(active);
                            }}
                        >
                            <Selection mode="single" selectAllMode="allPages" showCheckBoxesMode={true} />
                            <Scrolling useNative={false} />
                            <LoadPanel enabled={true} />
                            <Column dataField="CAS_REF" visible={true} caption={Dict.get('K.No')} />
                            <Column dataField="CAS_TIP" caption={Dict.get('Satış Tipi')}>
                                <Lookup dataSource={satisTipiItems()} searchEnabled={true} searchMode='contains' displayExpr='ST_AD' searchExpr='ST_AD' valueExpr='ST_KODINT' />
                            </Column>
                            <Column dataField="CAS_SATIS_TURU" caption={Dict.get('Satış Türü')}>
                                <Lookup dataSource={allSatisTuruLookupItems()} searchEnabled={true} searchMode='contains' displayExpr='ST_AD' searchExpr='ST_AD' valueExpr='ST_REF' />
                            </Column>
                            <Column dataField="CAS_SAT_PAKET_REF" caption={Dict.get('Satış Paketi')}>
                                <Lookup dataSource={allSatisPaketLookupItems()} searchEnabled={true} searchMode='contains' displayExpr='ST_AD' searchExpr='ST_AD' valueExpr='ST_REF' />
                            </Column>
                            <Column dataField="CAS_SATIS_TUT_TAR" dataType="date" format="dd-MM-yyyy" caption={Dict.get('Satış Tarihi')} />
                            <Column dataField="CAS_GIR_TAR" dataType="date" format="dd-MM-yyyy" caption={Dict.get('Tedavi Tarihi')} />
                        </DataGrid>
                        )}
                    </div>

                    {/* İşlem Paneli */}
                    {activeSatis && activeSatis.CAS_REF > 0 && (() => {
                        const satistipigrubu = getIslemStTur(activeSatis.CAS_TIP) ?? -1;
                        return (
                            <div style={{
                                padding: 12,
                                background: 'var(--dx-color-bg-secondary, #f8f9fa)',
                                border: '1px solid var(--dx-color-border, #dee2e6)',
                                borderRadius: 4
                            }}>
                                <IslemPanel
                                    key={activeSatis.CAS_REF}
                                    islemReadOnly={false}
                                    carRef={carRef}
                                    casRef={activeSatis.CAS_REF}
                                    satistipigrubu={satistipigrubu}
                                    satisTipi={activeSatis.CAS_TIP || -1}
                                    satisTuru={activeSatis.CAS_SATIS_TURU}
                                    islemRefreshToken={islemRefreshToken}
                                />
                            </div>
                        );
                    })()}
            </div>
        </ScrollView>
        </div>
    );

    if (isEmbedded) {
        return <div style={{ width: '100%', height: '100%' }}>{content}</div>;
    }

    return (
        <Popup
            visible={formShow}
            onHiding={handleHiding}
            dragEnabled={true}
            showCloseButton={true}
            width="100%"
            height="100%"
            shading={false}
            title={`İşlem Girişi (${carRef || ''}${carAdSoy ? ' - ' + carAdSoy : ''})`}
        >
            <Position my="center" at="center" of={window} />
            {content}
        </Popup>
    );
}
